import type { AdminVehicleGenerationDto } from '@carcheck/shared';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Button from '@mui/material/Button';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import type { GenerationConflictHit } from '../_lib/catalogErrors';

/**
 * 409 `catalog_generation_conflict` (AUT-450 / VG-03): «Такое поколение уже
 * есть: <display>». The holder is looked up in the (already refetched) list;
 * a hidden one offers «Показать снова» — un-hide it instead of adding a twin.
 * Without details (a unique-index race) or when the row is not in the list —
 * the generic line, no action.
 */
export interface GenerationConflictAlertProps {
  hit: GenerationConflictHit | null;
  items: readonly AdminVehicleGenerationDto[];
  busy: boolean;
  onRestore: (holder: AdminVehicleGenerationDto) => void;
}

const K = 'vehicleCatalog.generations.conflict';

export const GenerationConflictAlert = ({
  hit,
  items,
  busy,
  onRestore,
}: GenerationConflictAlertProps): ReactElement => {
  const { t } = useTranslation();
  const holder = hit ? (items.find((g) => g.id === hit.generationId) ?? null) : null;
  const hidden = holder?.status === 'hidden';
  return (
    <Alert
      severity="error"
      action={
        hidden && holder ? (
          <Button
            size="small"
            color="inherit"
            variant="outlined"
            disabled={busy}
            onClick={() => onRestore(holder)}
          >
            {t(`${K}.restore`)}
          </Button>
        ) : undefined
      }
    >
      <AlertTitle>
        {holder ? t(`${K}.title`, { display: holder.display }) : t(`${K}.generic`)}
      </AlertTitle>
      {hidden ? t(`${K}.hiddenBody`) : t(`${K}.body`)}
    </Alert>
  );
};
